import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Shield, Award, Users, CheckCircle, Moon, Building2, CalendarDays } from 'lucide-react';
import { services } from '../data/content';

const iconComponents: Record<string, React.ElementType> = {
  moon: Moon,
  building: Building2,
  calendar: CalendarDays,
};

export function Leistungen() {
  return (
    <>
      <SEO 
        title="Leistungen"
        description="Night-Auditor-Service, Receptionsservice und Event-Service für Hotellerie und Veranstaltungen. Professionell, zuverlässig, erfahren."
        path="/leistungen"
      />

      {/* Header Section */}
      <section className="pt-32 pb-20 bg-brand-950">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-platin-400 font-semibold text-sm uppercase tracking-widest">
              Unsere Expertise
            </span>
            <h1 className="heading-xl text-white mt-4 mb-6">
              Professionelle Dienstleistungen für Ihren Erfolg
            </h1>
            <p className="text-xl text-platin-200 leading-relaxed">
              Von der nächtlichen Abrechnung bis zum Event-Support – wir bieten 
              maßgeschneiderte Lösungen für Hotels und Veranstaltungen.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {services.map((service, index) => {
            const IconComponent = iconComponents[service.icon] || Building2;
            return (
              <div 
                key={service.id}
                id={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index !== services.length - 1 ? 'mb-24' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                    index === 0 ? 'bg-brand-950 text-platin-200' : 'bg-platin-100 text-brand-600'
                  }`}>
                    <IconComponent className="w-8 h-8" />
                  </div>
                  <h2 className="heading-md text-brand-950 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-body mb-8">
                    {service.description}
                  </p>
                  
                  <div className="space-y-4 mb-8">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <CheckCircle className="w-5 h-5 text-platin-500 shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/kontakt"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Anfragen
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>

                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-gradient-to-br from-platin-100 to-white rounded-2xl p-8 lg:p-12 shadow-soft">
                    <div className="aspect-video bg-brand-100 rounded-xl flex items-center justify-center overflow-hidden mb-8">
                      <img
                        src={`/images/leistung${index + 1}.jpeg`}
                        alt={`${service.title} - Visualisierung`}
                        className="w-full h-full object-cover"
                        width="800"
                        height="450"
                        loading={index === 0 ? "eager" : "lazy"}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div class="text-center text-brand-600">
                              <div class="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-soft">
                                <svg class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <p class="font-semibold text-lg">${service.title}</p>
                            </div>
                          `;
                        }}
                      />
                    </div>

                    {/* Service-specific highlights */}
                    <div className="grid grid-cols-2 gap-4">
                      {service.id === 'night-audit' && (
                        <>
                          <div className="bg-white p-4 rounded-xl border border-platin-200">
                            <Clock className="w-6 h-6 text-brand-600 mb-2" />
                            <p className="font-semibold text-brand-950">22:00 - 06:00</p>
                            <p className="text-sm text-gray-600">Standard-Einsatzzeit</p>
                          </div>
                          <div className="bg-white p-4 rounded-xl border border-platin-200">
                            <Shield className="w-6 h-6 text-brand-600 mb-2" />
                            <p className="font-semibold text-brand-950">100%</p>
                            <p className="text-sm text-gray-600">Diskretion garantiert</p>
                          </div>
                        </>
                      )}

                      {service.id === 'rezeption' && (
                        <>
                          <div className="bg-white p-4 rounded-xl border border-platin-200">
                            <Users className="w-6 h-6 text-brand-600 mb-2" />
                            <p className="font-semibold text-brand-950">Mehrsprachig</p>
                            <p className="text-sm text-gray-600">DE, EN, FR, ES</p>
                          </div>
                          <div className="bg-white p-4 rounded-xl border border-platin-200">
                            <Award className="w-6 h-6 text-brand-600 mb-2" />
                            <p className="font-semibold text-brand-950">Zertifiziert</p>
                            <p className="text-sm text-gray-600">Professioneller Service</p>
                          </div>
                        </>
                      )}

                      {service.id === 'events' && (
                        <>
                          <div className="bg-white p-4 rounded-xl border border-platin-200">
                            <Users className="w-6 h-6 text-brand-600 mb-2" />
                            <p className="font-semibold text-brand-950">50-5000+</p>
                            <p className="text-sm text-gray-600">Teilnehmer</p>
                          </div>
                          <div className="bg-white p-4 rounded-xl border border-platin-200">
                            <Clock className="w-6 h-6 text-brand-600 mb-2" />
                            <p className="font-semibold text-brand-950">Flexibel</p>
                            <p className="text-sm text-gray-600">Nach Bedarf</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PMS Systems Section */}
      <section className="section-padding bg-platin-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="heading-md text-brand-950 mb-4">
              Erfahrung mit führenden Hotelsystemen
            </h2>
            <p className="text-body">
              Wir arbeiten professionell mit allen gängigen Property Management Systemen 
              und garantieren eine reibungslose Integration.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {['Opera (Accor)', 'Opera Express', 'Lean', 'SIHOT', 'Protel'].map((system) => (
              <div 
                key={system}
                className="bg-white border border-platin-200 rounded-xl p-6 text-center font-medium text-brand-950 hover:border-platin-400 hover:shadow-soft transition-all"
              >
                {system}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-brand-950">
        <div className="container-custom text-center">
          <h2 className="heading-md text-white mb-6">
            Benötigen Sie ein individuelles Angebot?
          </h2>
          <p className="text-platin-200 text-lg max-w-2xl mx-auto mb-8">
            Jeder Betrieb ist einzigartig. Kontaktieren Sie uns für eine persönliche 
            Beratung und ein maßgeschneidertes Angebot.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 bg-platin-200 text-brand-950 px-8 py-4 rounded-lg font-semibold hover:bg-white transition-colors"
            >
              Kontakt aufnehmen
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`tel:${'+4917672536937'.replace(/\s/g, '')}`}
              className="inline-flex items-center justify-center gap-2 border-2 border-platin-400/50 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              Direkt anrufen
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
